import React, { useEffect, useRef, useState } from 'react';
import type { AnalysisResult, ApiClient } from '../types';
import ResultsView from './ResultsView';
import useMediaControls from '../hooks/useMediaControls';
import useWordTimestamps from '../hooks/useWordTimestamps';
import useMediaSync from '../hooks/useMediaSync';
import formatTime from '../utils/formatTime';

interface Props {
  result: AnalysisResult | null;
  videoObjectUrl: string | null;
  audioObjectUrl: string | null;
  onBack: () => void;
  loading: boolean;
  loadingMessage?: string;
  error?: string | null;
  categoryName?: string;
  apiClient: ApiClient;
  updateHistoryChat?: (item: AnalysisResult, chat: Array<{ role: string; content: string }>) => void;
}

const ResultsContainer: React.FC<Props> = ({
  result,
  videoObjectUrl,
  audioObjectUrl,
  onBack,
  loading,
  loadingMessage,
  error,
  categoryName,
  apiClient,
  updateHistoryChat,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const media = useMediaControls({ videoRef, audioRef });

  const { wordTimestamps } = useWordTimestamps(result as AnalysisResult | null);
  const videoSync = useMediaSync(videoRef, wordTimestamps, true);
  const audioSync = useMediaSync(audioRef, wordTimestamps, true);

  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedWordIndex(null);
  }, [result]);

  useEffect(() => {
    if (result?.category === 'audio_transcription') {
      setSelectedWordIndex(audioSync.selectedWordIndex);
    } else {
      setSelectedWordIndex(videoSync.selectedWordIndex);
    }
  }, [audioSync.selectedWordIndex, videoSync.selectedWordIndex, result?.category]);

  const seekTo = (s?: number) => {
    if (typeof s !== 'number' || Number.isNaN(s)) return;
    try {
      if (result?.category === 'audio_transcription') {
        const a = audioRef.current;
        if (a) {
          a.currentTime = Math.max(0, s);
          a.play().catch(() => undefined);
          return;
        }
      }
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = Math.max(0, s);
      v.play().catch(() => undefined);
    } catch (e) {
      // ignore
    }
  };

  const toggleAudioPlay = () => {
    if (media.playing) media.pause(); else media.play().catch(() => undefined);
  };

  const onAudioSeek = (v: number) => audioSync.seekTo(v);

  const skipAudio = (secs: number) => {
    const t = audioSync.currentTime || 0;
    audioSync.seekTo(Math.max(0, Math.min((audioSync.duration || 0), t + secs)));
  };

  const handleSetSelectedWordIndex = (index: number | null) => {
    setSelectedWordIndex(index);
    if (index === null) return;
    const ts = wordTimestamps && Array.isArray(wordTimestamps) ? wordTimestamps[index] : null;
    if (!ts) return;
    if (result?.category === 'audio_transcription') {
      audioSync.seekTo(ts.start);
    } else {
      videoSync.seekTo(ts.start);
    }
  };

  return (
    <ResultsView
      result={result}
      loading={loading}
      loadingMessage={loadingMessage || ''}
      error={error || null}
      categoryName={categoryName}
      onBack={onBack}
      videoObjectUrl={videoObjectUrl}
      audioObjectUrl={audioObjectUrl}
      videoRef={videoRef}
      audioRef={audioRef}
      audioPlaying={media.playing}
      audioCurrentTime={media.currentTime}
      audioDuration={media.duration}
      wordTimestampsState={wordTimestamps}
      selectedWordIndex={selectedWordIndex}
      onSeekTo={seekTo}
      onSetSelectedWordIndex={handleSetSelectedWordIndex}
      onToggleAudioPlay={toggleAudioPlay}
      onAudioSeek={onAudioSeek}
      onSkipAudio={skipAudio}
      onFormatTime={formatTime}
      apiClient={apiClient}
      updateHistoryChat={updateHistoryChat}
    />
  );
};

export default ResultsContainer;
